import { useSyncExternalStore } from "react";

/**
 * Old sessions move out of the main lists into "Archived" so Home stays short as it grows. A session is archived when nothing has
 * happened in it for longer than the chosen time (output, typing, or when it started if that is all there is). The choice is the
 * person's, kept on this phone; "Never" turns archiving off.
 */
/** `label` reads in a sentence ("Quiet for over 24 hours"); `short` fits the five-way switch in Settings. */
export const ARCHIVE_OPTIONS: { days: number; label: string; short: string }[] = [
  { days: 1, label: "24 hours", short: "24 h" },
  { days: 3, label: "3 days", short: "3 d" },
  { days: 7, label: "1 week", short: "1 wk" },
  { days: 30, label: "1 month", short: "1 mo" },
  { days: 0, label: "Never", short: "Never" },
];
export const DEFAULT_ARCHIVE_DAYS = 1;
const KEY = "foreman.archive.days";

/** Has this session been quiet for longer than `days` (0: never archived)? */
export function isArchived(session: { startedAt: number; lastActivityAt?: number }, now: number, days: number): boolean {
  if (!(days > 0)) return false;
  return now - (session.lastActivityAt ?? session.startedAt) > days * 86_400_000;
}

/** A stored value read back safely: only the offered choices count. */
export function parseArchiveDays(raw: string | null | undefined): number {
  const n = raw === null || raw === undefined ? NaN : Number(raw);
  return ARCHIVE_OPTIONS.some((o) => o.days === n) ? n : DEFAULT_ARCHIVE_DAYS;
}

const listeners = new Set<() => void>();
const read = (): number => {
  try {
    return parseArchiveDays(localStorage.getItem(KEY));
  } catch {
    return DEFAULT_ARCHIVE_DAYS;
  }
};
let cached: number | null = null;
const get = (): number => (cached ??= read());

export function setArchiveDays(days: number): void {
  cached = parseArchiveDays(String(days));
  try {
    localStorage.setItem(KEY, String(cached));
  } catch {
    /* the choice still holds until the app closes */
  }
  for (const l of [...listeners]) l();
}

const subscribe = (cb: () => void) => {
  listeners.add(cb);
  return () => listeners.delete(cb);
};
export const useArchiveDays = (): number => useSyncExternalStore(subscribe, get, () => DEFAULT_ARCHIVE_DAYS);
